import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import Mic from "~icons/ph/microphone";
import Square from "~icons/ph/square";
import X from "~icons/ph/x";
import { realtime } from "../data/realtime";
import { t } from "../i18n";
import { Modal, Wave } from "../ui/components";
import { AudioMeter } from "./audio-meter";
import { DevicePickers } from "./device-pickers";
import { captureMedia, mediaError } from "./devices";
import { watchVoiceLevel } from "./voice-level";

type Check =
  | { phase: "starting" | "testing" }
  | { phase: "stopped"; failure?: string };

const statuses = {
  stopped: "الاختبار متوقف. اضغط «بدء الاختبار» للمتابعة.",
  starting: "اسمح باستخدام الميكروفون إذا طلب المتصفح ذلك.",
  testing: "الميكروفون يعمل. تحدث وشاهد الموجة.",
};
const actions = {
  stopped: "بدء الاختبار",
  starting: "جارٍ تشغيل الميكروفون…",
  testing: "إيقاف الاختبار",
};

export function MicrophoneCheck({ onClose }: { onClose: () => void }) {
  const [check, setCheck] = useState<Check>({ phase: "starting" });
  const [level, setLevel] = useState(0);
  const stream = useRef<MediaStream | null>(null),
    stopMeter = useRef<(() => void) | null>(null),
    generation = useRef(0);
  const release = useCallback(() => {
    generation.current++;
    stopMeter.current?.();
    stopMeter.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    setLevel(0);
  }, []);
  const start = useCallback(async () => {
    release();
    const token = generation.current;
    setCheck({ phase: "starting" });
    try {
      const media = await captureMedia();
      if (token !== generation.current) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = media;
      stopMeter.current = watchVoiceLevel(media, setLevel);
      media.getAudioTracks()[0]?.addEventListener(
        "ended",
        () => {
          if (token !== generation.current) return;
          release();
          setCheck({
            phase: "stopped",
            failure: "توقف الميكروفون. أعد تشغيل الاختبار للمحاولة مجددًا.",
          });
        },
        { once: true },
      );
      setCheck({ phase: "testing" });
    } catch (error) {
      if (token !== generation.current) return;
      release();
      setCheck({ phase: "stopped", failure: mediaError(error, false) });
    }
  }, [release]);
  useEffect(() => {
    void start();
    return release;
  }, [start, release]);
  const close = useEffectEvent(onClose);
  useEffect(() => {
    const leave = () => close();
    const unsubscribe = realtime.subscribe((event) => {
      if (event.type === "session-ended") leave();
    });
    window.addEventListener("pagehide", leave);
    return () => {
      unsubscribe();
      window.removeEventListener("pagehide", leave);
    };
  }, []);
  const testing = check.phase === "testing";
  return (
    <Modal
      title={t("اختبار الميكروفون")}
      onClose={onClose}
      className="voice-modal mic-test"
      dismissible={false}
    >
      <span className="modal-kicker">
        <Wave /> {t("اختبار الميكروفون")}
      </span>
      <div className="call-avatar">
        <Mic width={40} height={40} />
      </div>
      <h2>{t("اختبار الميكروفون")}</h2>
      <p className="voice-state" role="status">
        {t(
          (check.phase === "stopped" && check.failure) || statuses[check.phase],
        )}
      </p>
      <AudioMeter level={level} />
      <div className="call-controls">
        <button
          className={testing ? "off" : "accept-call"}
          disabled={check.phase === "starting"}
          onClick={() => {
            if (testing) {
              release();
              setCheck({ phase: "stopped" });
            } else void start();
          }}
        >
          <span>
            {testing ? (
              <Square width={20} height={20} />
            ) : (
              <Mic width={22} height={22} />
            )}
          </span>
          {t(actions[check.phase])}
        </button>
        <button onClick={onClose}>
          <span>
            <X width={22} height={22} />
          </span>
          <small>{t("إغلاق")}</small>
        </button>
      </div>
      <DevicePickers
        testing={testing}
        onMicrophone={() => {
          if (stream.current) void start();
        }}
      />
      <p className="mic-privacy">
        {t("يتم الاختبار على جهازك دون تسجيل الصوت.")}
      </p>
    </Modal>
  );
}
